import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { toJS, action } from 'mobx'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'
import pick from 'lodash/pick'
import get from 'lodash/get'
import set from 'lodash/set'

import { expertApi, getCvSourceSuggestions } from '@atheneum/api'
import { getSubIndustryOptions } from '@atheneum/api/dist/lib'

import ExpertExperienceForm from './ExpertExperienceForm'
import GetContactInfoSection from './GetContactInfoSection'
import getExpertValidationSchema from './ExpertFormValidationSchema'

import Button from '@components/Button'
import Checkbox from '@components/Form/Checkbox'
import AddressFields from '@components/Form/AddressFields'
import {
  Dropdown,
  TagsInput,
  Autocomplete
} from '@components/Form/Autocomplete'
import Input from '@components/Form/Input'
import PhoneFields from '@components/Form/PhoneFields'
import EmailFields from '@components/Form/EmailFields'
import LanguageFields from '@components/Form/LanguageFields'
import RichTextEditor from '@components/Form/RichTextEditor'
import ProfessionalLinkFields from '@components/Form/ProfessionalLinkFields'
import WarningIcon from '@components/Icon/Warning'
import Lusha from '@components/Icon/Lusha'
import Action from '@components/Action'
import InfoBox from '@components/InfoBox'
import Tooltip from '@components/Tooltip/Tooltip'


class ExpertForm extends Component {
  static propTypes = {
    expert: PropTypes.object,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    expertStore: PropTypes.object,
    athenaList: PropTypes.bool,
    history: PropTypes.object,
    userStore: PropTypes.object,
    growlStore: PropTypes.object
  }

  state = {
    showGetContactInfoSection: false
  }

  getDuplicateExpertNames = action(fullName => {
    expertApi.duplicateExpert(fullName).then(experts => {
      const warningMessage =
        experts.length > 0
          ? "Expert with this name already exists. Please make sure it's not a duplicate."
          : null
      this.warning = warningMessage
    })
  })

  handleTitleChange = (title, form) => {
    form.setFieldValue('title', title, false)
  }

  handleFirstNameChange = (firstName, form) => {
    form.setFieldValue('firstName', firstName, false)
    if (form.values.lastName) {
      const fullName = firstName + ' ' + form.values.lastName
      this.getDuplicateExpertNames(fullName)
    }
  }

  handleLastNameChange = (lastName, form) => {
    form.setFieldValue('lastName', lastName, false)
    if (form.values.firstName) {
      const fullName = form.values.firstName + ' ' + lastName
      this.getDuplicateExpertNames(fullName)
    }
  }

  renderIndustrySubindustryItem = industryOrSubindustry => {
    const isIndustry = industryOrSubindustry.type === 'industry'
    if (isIndustry) {
      return (
        <div>
          <b>{industryOrSubindustry.name}</b>
        </div>
      )
    }

    return (
      <div className="autocomplete__item--suboption">
        {industryOrSubindustry.name}
      </div>
    )
  }

  handleAmountChange = (amount, form) => {
    if (amount === '') {
      form.setFieldValue('expert.hourlyRate', null, false)
    } else {
      form.setFieldValue('expert.hourlyRate', amount, false)
    }
  }

  saveExpert = expertData => {
    const { expert } = this.props
    const languages = expertData.languages.map(language => {
      const languageId = language.id || language.languageToUser.languageId
      return { ...language, id: languageId }
    })

    // prevent empty repeatableInputs from generating validation errors by filtering them out
    expertData.address.emails = expertData.address.emails.filter(
      entry => entry.email !== ''
    )

    const nullable = [
      'hourlyRateCurrencyId',
      'hourlyRate',
      'expert.hourlyRateCurrencyId',
      'expert.hourlyRate'
    ]
    for (const key of nullable) {
      if (!get(expertData, key)) set(expertData, key, null)
    }

    const savedData = { ...expertData, languages: languages }

    if (!expert) {
      // prevent industry experience getting copied into this field on creation (expert.service.js:63)
      savedData.expert.expertIndustryExperience = ' '
    }

    if (expert?.linkedinVanityName) {
      savedData.expert.linkedinVanityName = expert.linkedinVanityName
    }

    if (this.props.athenaList) {
      return expertApi.create(savedData).then(res => this.props.onCreate(res))
    } else {
      return expert && Number(expert.id)
        ? expertApi.updateV2(expert.id, savedData, expert)
        : expertApi.create(savedData)
    }
  }

  renderGetContactInfoAction = form => {
    const hasName = form.values.firstName && form.values.lastName
    const hasCurrentExperience = form.values.experiences.some(
      ({ current }) => !!current
    )

    const isDisabled = !(hasName && hasCurrentExperience)
    return (
      <div className="action-wrapper">
        <Action
          disabled={isDisabled}
          onClick={() =>
            this.setState(state => ({
              showGetContactInfoSection: !state.showGetContactInfoSection
            }))
          }>
          <Lusha />
          <span>Get contact info</span>
        </Action>
        <Tooltip tooltip="First name, last name, and at least one work experience marked current have to be defined before you can get contact info" />
      </div>
    )
  }

  handleCvSourceAutocomplete = input => {
    if (!input) return Promise.resolve([])
    return getCvSourceSuggestions(input)
      .then(sources => {
        return sources
      })
      .catch(err => {
        console.error(err)
      })
  }

  render() {
    const { expert, modalStore, staticData, expertStore, history, userStore } =
      this.props
    const { showGetContactInfoSection } = this.state
    const { PERMISSIONS, PHONE_TYPES } = staticData.CONST
    const isAdminOrERM = userStore.user.permissions.find(
      p =>
        p.id === PERMISSIONS.ADMIN ||
        p.id === PERMISSIONS.ADVANCED_EXPERT_MANAGEMENT
    )

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={initializeForm({ expert, staticData })}
          validationSchema={getExpertValidationSchema(expert, staticData)}
          onSubmit={(values, actions) => {
            this.saveExpert(values)
              .then(savedExpert => {
                expert && Number(expert.id)
                  ? expertStore.get(savedExpert.id)
                  : history.push('/expert/' + savedExpert.id)
                modalStore.close()
              })
              .catch(({ message }) => {
                this.error = message
                actions.setSubmitting(false)
              })
          }}
          render={form => {
            window.form = form
            const phones = get(form, 'values.address.phones', [])

            const expertPhone = {
              phoneTypeId: get(expert, 'expert.phoneTypeId', 0),
              phoneNum: get(expert, 'expert.phoneNum', '')
            }

            const disabledRemindersCheckbox =
              !phones.find(
                ({ phoneTypeId, phoneNum }, i) =>
                  phoneTypeId === PHONE_TYPES.MOBILE && phoneNum
              ) &&
              (expertPhone.phoneTypeId !== PHONE_TYPES.MOBILE ||
                !expertPhone.phoneNum)

            if (
              disabledRemindersCheckbox &&
              form.values.expert?.sendSmsReminders
            ) {
              form.setFieldValue('expert.sendSmsReminders', false, false)
            }

            return (
              <div className="expert-form">
                {!this.props.athenaList && (
                  <h1 className="expert-form__title">
                    {expert ? 'Edit expert profile' : 'Create expert'}
                  </h1>
                )}
                {this.error ? (
                  <InfoBox theme="red" icon={<WarningIcon />}>
                    {this.error}
                  </InfoBox>
                ) : null}
                {this.warning ? (
                  <InfoBox theme="yellow" icon={<WarningIcon />}>
                    {this.warning}
                  </InfoBox>
                ) : null}

                <Form onSubmit={form.handleSubmit}>
                  <div className="expert-form__section">
                    <h2 className="expert-form__subtitle">
                      Personal information
                    </h2>
                    <Field
                      name="title"
                      label="Title *"
                      data-cy="title-dropdown"
                      placeholder="Select title"
                      onChange={title => this.handleTitleChange(title, form)}
                      options={staticData.titles}
                      component={Dropdown}
                    />
                    <Field
                      name="firstName"
                      label="First name *"
                      onChange={e =>
                        this.handleFirstNameChange(e.target.value, form)
                      }
                      component={Input}
                    />
                    <Field
                      name="lastName"
                      label="Last name *"
                      onChange={e =>
                        this.handleLastNameChange(e.target.value, form)
                      }
                      component={Input}
                    />
                    {!this.props.athenaList && (
                      <Field
                        name="originalName"
                        label="Original name"
                        component={Input}
                      />
                    )}
                    <AddressFields
                      data-cy="addressfields-autocomplete"
                      form={form}
                      name="address"
                      timezone
                      cityLabel="City *"
                    />
                  </div>
                  <div className="expert-form__section">
                    <h2 className="expert-form__subtitle">
                      Professional information
                    </h2>
                    <Field
                      data-cy="industry-subindustry-dropdown"
                      name="expert.subIndustries"
                      label="Industry / subindustry *"
                      value={form.values.expert.subIndustries?.length && form.values.expert.subIndustries[0].id === 133 ? [] : form.values.expert.subIndustries || []}
                      itemToString={item => item && item.name}
                      renderItem={this.renderIndustrySubindustryItem}
                      options={getSubIndustryOptions(staticData)}
                      component={TagsInput}
                    />
                    <LanguageFields
                      form={form}
                      name="languages"
                      label="Languages *"
                    />
                    <Field
                      data-cy="industry-experience-richtext"
                      name="expert.industryExpertise"
                      label="Industry experience *"
                      placeholder="Industry experience that will be visible on the Client interface and not visible to Expert"
                      toolbarRight
                      component={RichTextEditor}
                    />

                    {!this.props.athenaList && (
                      <>
                        <Field
                          name="expert.expertIndustryExperience"
                          label={
                            <div>
                              Expert industry experience
                              <Tooltip tooltip="Cannot edit expert experience. This information is present on expert interface and is fully manageable by experts. If you require changes, please contact ERM team." />
                            </div>
                          }
                          placeholder="Expert industry experinece will be visible to Expert"
                          toolbarRight
                          disabled={!isAdminOrERM}
                          component={RichTextEditor}
                        />
                        <div className="expert-form__input-group">
                          <label className="expert-form__label">
                            Hourly rate
                          </label>
                          <div className="expert-form__input-wrapper expert-form__input-wrapper--two_one_children">
                            <Field
                              name="expert.hourlyRateCurrencyId"
                              data-cy="currencySelectButton"
                              placeholder="Currency"
                              itemToString={item => item && item.name}
                              options={staticData.currencies}
                              component={Dropdown}
                            />
                            <Field
                              name="expert.hourlyRate"
                              placeholder="Amount"
                              onChange={e =>
                                this.handleAmountChange(e.target.value, form)
                              }
                              component={Input}
                            />
                          </div>
                        </div>
                        <div className="expert-form__input-group">
                          <Field
                            name="expert.currentUsGovernmentEmployee"
                            label="Current US Government employee"
                            component={Checkbox}
                          />

                        </div>
                      </>
                    )}
                  </div>
                  <ExpertExperienceForm form={form} />
                  <div className="expert-form__section">
                    {!this.props.athenaList && (
                      <>
                        <h2 className="expert-form__subtitle expert-form__subtitle--with-action">
                          Contact information
                          {expert
                            ? null
                            : this.renderGetContactInfoAction(form)}
                        </h2>
                        {expert ? null : (
                          <GetContactInfoSection
                            show={showGetContactInfoSection}
                            form={form}
                          />
                        )}
                        <PhoneFields
                          form={form}
                          name="address.phones"
                          label={
                            <div>
                              Phones
                              <Tooltip
                                tooltip={staticData.phoneNumberTooltipText}
                              />
                            </div>
                          }
                          phoneNotRepeating={form.errors.phoneNotRepeating}
                        />
                      </>
                    )}
                    <EmailFields
                      form={form}
                      athenaList={this.props.athenaList}
                      primaryEmail="email"
                      additionalEmails="address.emails"
                      label="Emails *"
                      emailNotRepeating={form.errors.emailNotRepeating}
                    />
                    {!this.props.athenaList && (
                      <ProfessionalLinkFields
                        form={form}
                        name="links"
                        label="Professional links"
                      />
                    )}
                    <ProfessionalLinkFields
                      form={form}
                      name="links"
                      label="Professional links"
                    />
                  </div>
                  {!this.props.athenaList && (
                    <>
                      <div className="expert-form__section">
                        <Field
                          creatable
                          itemFromCreatable={item => ({
                            name: item,
                            id: item,
                            isCreatable: true
                          })}
                          name={'cv.cvSource'}
                          label={'Source'}
                          placeholder=""
                          itemToString={item => item && item.name}
                          loadOptions={this.handleCvSourceAutocomplete}
                          component={Autocomplete}
                        />
                        <div>
                          <Field
                            name="expert.newsletter"
                            label="Subscribe to newsletter"
                            component={Checkbox}
                          />
                        </div>
                        <div>
                          <Field
                            name="expert.sendSmsReminders"
                            label="Interview reminders via SMS"
                            tooltip="SMS will only be sent to numbers saved as mobile phone"
                            component={Checkbox}
                            disabled={disabledRemindersCheckbox}
                          />
                        </div>
                        <p className="expert-form__label">
                          Fields marked with * are required
                        </p>
                      </div>
                      <div className="expert-form__section p-b-0">
                        <Button
                          primary
                          submit
                          onClick={this.onSubmit}
                          disabled={form.isSubmitting}>
                          Save
                        </Button>
                        <Button onClick={modalStore.close}>Cancel</Button>
                      </div>
                    </>
                  )}
                  {this.props.athenaList && (
                    <div className="expert-form__section p-b-0">
                      <Button
                        primary
                        submit
                        onClick={this.onSubmit}
                        disabled={form.isSubmitting}>
                        Save
                      </Button>
                      <Button onClick={() => this.props.onDropExpert()}>
                        Drop expert
                      </Button>
                      <Button onClick={() => this.props.onExit()}>Exit</Button>
                    </div>
                  )}
                </Form>
              </div>
            )
          }}
        />
      </div>
    )
  }
}

ExpertForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'modalStore',
    'staticData',
    'userStore',
    'growlStore'
  )(observer(ExpertForm))
)

function initializeForm({ expert, staticData }) {
  const initialValues = expert
    ? getInitialExpertValues(expert)
    : {
      title: '',
      firstName: '',
      lastName: '',
      originalName: '',
      address: {
        city: '',
        state: '',
        countryId: null,
        timezoneId: null,
        phones: [
          {
            phoneTypeId: staticData.CONST.PHONE_TYPES.OFFICE,
            phoneNum: ''
          }
        ],
        emails: []
      },
      email: '',
      links: [],
      expert: {
        subIndustries: [],
        industryExpertise: '',
        expertIndustryExperience: '',
        hourlyRateCurrencyId: '',
        hourlyRate: null,
        newsletter: false,
        sendSmsReminders: false,
        currentUsGovernmentEmployee: false,
      },
      languages: [
        {
          id: '',
          name: '',
          languageToUser: {
            languageId: '',
            languageProficiencyId: ''
          }
        }
      ],
      tags: [],
      experiences: [
        {
          position: '',
          company: '',
          location: '',
          startDate: null,
          endDate: null,
          current: false,
          description: ''
        }
      ],
      phoneTypeId: null,
      phoneNum: ''
    }
  return initialValues
}

function getInitialExpertValues(expert) {
  const initialExpertValues = pick(toJS(expert), [
    'title',
    'firstName',
    'lastName',
    'originalName',
    'address',
    'email',
    'links',
    'expert.subIndustries',
    'languages',
    'tags',
    'expert.industryExpertise',
    'expert.expertIndustryExperience',
    'expert.hourlyRateCurrencyId',
    'expert.hourlyRate',
    'expert.newsletter',
    'cv',
    'expert.sendSmsReminders',
    'experiences',
    'expert.phoneTypeId',
    'expert.phoneNum',
    'expert.currentUsGovernmentEmployee'
  ])
  // There are some experts with no associated languages which should no longer be possible
  if (!initialExpertValues.languages?.length) {
    initialExpertValues.languages = [
      {
        id: '',
        name: '',
        languageToUser: {
          languageId: '',
          languageProficiencyId: ''
        }
      }
    ]
  }

  initialExpertValues.experiences = get(
    initialExpertValues,
    'cv.experiences',
    []
  )
  return initialExpertValues
}
